import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Modal, Input, Select, Table, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetMatGapMaster } from '../../hooks/genericHooks';
import type { MatGapMaster } from '../../types';

export type MatGapMasterMaterialRow = {
  mat_id: string;
  description: string;
  unit: string;
  prod_group: string | null;
  ext_group: string | null;
  prod_group_desc: string | null;
  ext_group_desc: string | null;
  hierarchy_long: string | null;
};

export interface MatGapMasterMaterialSelectProps {
  open: boolean;
  onOk: (value: any) => void; // returns based on returnMode
  onCancel: () => void;
  title?: string;
  /**
   * Controls the shape of the selected value returned on OK.
   * - 'basic': a single string `${mat_id} ${description}`
   * - 'code' : a single string `${mat_id}`
   * - 'duel' : a tuple [mat_id, description, unit]
   * - 'full' : an array with a single object MatGapMasterMaterialRow (default)
   */
  returnMode?: 'basic' | 'code' | 'duel' | 'full';
}

const MatGapMasterMaterialSelect: React.FC<MatGapMasterMaterialSelectProps> = ({ open, onOk, onCancel, title, returnMode = 'basic' }) => {
  const { data: resp, isLoading } = useGetMatGapMaster() as any;
  const master: MatGapMaster[] = Array.isArray(resp) ? resp : (resp?.data ?? []);

  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [extGroupFilter, setExtGroupFilter] = useState<string>('');
  // Pagination state
  const [pageSize, setPageSize] = useState<number>(100);
  const [current, setCurrent] = useState<number>(1);

  // Debounce search to avoid filtering 32k rows on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const debounceTimer = useRef<number | null>(null);
  useEffect(() => {
    if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
    // @ts-ignore - setTimeout typing for number id in browsers
    debounceTimer.current = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => { if (debounceTimer.current) window.clearTimeout(debounceTimer.current); };
  }, [search]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const norm = (x: any) => (x == null ? '' : String(x)).trim().toLowerCase();

  const filtered = useMemo(() => {
    const searchVal = (debouncedSearch || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const matchesSearch = (r: any) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const shortDesc = r["Malzeme kısa metni"] ?? '';
      const longDesc = r["Malzeme uzun tanımı"] ?? '';
      const unifiedDesc = r.description ?? '';

      const hay = `${unifiedDesc} ${shortDesc} ${longDesc}`.toLowerCase();
      if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
      return tokens.every(t => hay.includes(t));
    };
    return (master || [])
      // Filter by derived group description
      .filter((r: any) => {
        const d = (r["Mal grubu tanımı"] || r["Mal grubu"] || '').trim();
        return !groupFilter || norm(d) === norm(groupFilter);
      })
      // Filter by derived external group description
      .filter((r: any) => {
        const d = (r["Hrc.mal grubu tanımı"] || r["Harici mal grubu"] || '').trim();
        return !extGroupFilter || norm(d) === norm(extGroupFilter);
      })
      .filter(matchesSearch);
  }, [master, debouncedSearch, groupFilter, extGroupFilter]);

  const groupOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (master || []).forEach((r: any) => {
      const raw = ((r["Mal grubu tanımı"] || r["Mal grubu"]) ?? '').toString().trim();
      if (raw) { const k = raw.toLowerCase(); if (!byKey.has(k)) byKey.set(k, raw); }
    });
    return Array.from(byKey.values()).sort().map(v => ({ value: v, label: v }));
  }, [master]);
  const extGroupOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (master || []).forEach((r: any) => {
      const raw = ((r["Hrc.mal grubu tanımı"] || r["Harici mal grubu"]) ?? '').toString().trim();
      if (raw) { const k = raw.toLowerCase(); if (!byKey.has(k)) byKey.set(k, raw); }
    });
    return Array.from(byKey.values()).sort().map(v => ({ value: v, label: v }));
  }, [master]);

  const dataSource = useMemo(() => (filtered || []).map((r: any, idx: number) => ({
    ...r,
    key: String(r.mat_id ?? r.id ?? r["Malzeme"] ?? idx),
    mat_id: r.mat_id ?? r["Malzeme"],
    description: r.description ?? r["Malzeme kısa metni"] ?? r["Malzeme uzun tanımı"] ?? '',
    unit: r.unit ?? r["Temel ölçü birimi"] ?? '',
    prod_group: r.prod_group ?? r["Mal grubu"] ?? '',
    ext_group: r.ext_group ?? r["Harici mal grubu"] ?? '',
    // derived descs with fallback to codes
    prod_group_desc: (r["Mal grubu tanımı"] || r["Mal grubu"] || '') || undefined,
    ext_group_desc: (r["Hrc.mal grubu tanımı"] || r["Harici mal grubu"] || '') || undefined,
    hierarchy_long: r.hierarchy_long ?? r["Ürün hiyerarşisi"] ?? r["Hiyerarşi Tanımı"] ?? '',
  })), [filtered]);

  // Quick lookup for transformed rows by key
  const dataByKey = useMemo(() => {
    const m = new Map<string, any>();
    (dataSource || []).forEach((r: any) => m.set(String(r.key), r));
    return m;
  }, [dataSource]);

  // Reset to first page when filters/search change to avoid empty pages
  useEffect(() => { setCurrent(1); }, [debouncedSearch, groupFilter, extGroupFilter]);
  const fullMap = useMemo(() => {
    const m = new Map<string, any>();
    (master || []).forEach((r: any, idx: number) => m.set(String(r.mat_id ?? r.id ?? r['Malzeme'] ?? idx), r));
    return m;
  }, [master]);

  const columns: ColumnsType<any> = [
    { title: 'Material ID', dataIndex: 'mat_id', key: 'mat_id', width: 120 },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 80 },
    { title: 'Group Desc', dataIndex: 'prod_group_desc', key: 'prod_group_desc', width: 180 },
    { title: 'Ext Group Desc', dataIndex: 'ext_group_desc', key: 'ext_group_desc', width: 200 },
  ];

  const emitSelection = (r: any) => {
    if (returnMode === 'code') {
      onOk(String(r.mat_id));
      return;
    }
    if (returnMode === 'basic') {
      onOk(`${r.mat_id} ${r.description}`);
      return;
    }
    if (returnMode === 'duel') {
      onOk([String(r.mat_id), r.description, r.unit]);
      return;
    }
    // full
    onOk([
      {
        mat_id: r.mat_id,
        description: r.description,
        unit: r.unit,
        prod_group: r.prod_group,
        ext_group: r.ext_group,
        prod_group_desc: r.prod_group_desc ?? undefined,
        ext_group_desc: r.ext_group_desc ?? undefined,
        hierarchy_long: r.hierarchy_long,
      },
    ]);
  };

  const handleOk = () => {
    const key = selectedRowKeys[0];
    if (!key) { onOk([]); return; }
    const r: any = dataByKey.get(String(key));
    if (!r) { onOk([]); return; }
    emitSelection(r);
  };

  return (
    <Modal
      open={open}
      title={title || 'Select Material (Master)'}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Select"
      width={1000}
    >
      <Space style={{ marginBottom: 8 }} wrap>
        <Input
          placeholder='Search material/description... (supports multi-word AND and "phrases")'
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 320 }}
        />
        <Select
          placeholder="Group Desc"
          allowClear
          value={groupFilter || undefined}
          onChange={(v) => setGroupFilter(v || '')}
          style={{ width: 200 }}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) => (String(option?.label || '')).toLowerCase().includes(input.toLowerCase())}
          options={groupOptions}
        />
        <Select
          placeholder="Ext Group Desc"
          allowClear
          value={extGroupFilter || undefined}
          onChange={(v) => setExtGroupFilter(v || '')}
          style={{ width: 200 }}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) => (String(option?.label || '')).toLowerCase().includes(input.toLowerCase())}
          options={extGroupOptions}
        />
      </Space>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey={(r) => r.key}
        loading={isLoading}
        size="small"
        pagination={{ current, pageSize, showSizeChanger: true, pageSizeOptions: [50, 100, 200, 500], showTotal: () => `Filtered: ${dataSource.length} / Total: ${(master || []).length}`, onChange: (p, ps) => { setCurrent(p); setPageSize(ps); } }}
        rowSelection={{ type: 'radio', selectedRowKeys, onChange: (keys) => setSelectedRowKeys(Array.isArray(keys) ? [keys[keys.length-1]] : []) }}
        onRow={(record) => ({
          onDoubleClick: () => {
            setSelectedRowKeys([record.key]);
            emitSelection(record);
          },
        })}
        scroll={{ x: 'max-content', y: 420 }}
      />
    </Modal>
  );
};

export default MatGapMasterMaterialSelect;
